import { LosseLink, useLoaderData } from '@ubo/losse-sjedel'
import clsx from 'clsx'
import dayjs from 'dayjs'
import Animated from '~/components/elements/Animated'
import Content from '~/components/elements/Content'
import type { Page_Flexcontent_Flex_Hubspot } from '~/graphql/types'
import { type AlteredSimplePublicObjectWithAssociations, type ProfilePageLoaderData } from '~/services/hubspot.server'
import { phases } from './HubspotBanner'
import { useEffect } from 'react'

export default function HubspotMatches({ fields }: { fields: Page_Flexcontent_Flex_Hubspot }) {
  const { hubspotData: profile } = useLoaderData<ProfilePageLoaderData>()
  const currentPhase = phases.find((phase) => phase.id.some((id) => String(id) === profile.deals[0].properties.dealstage))
  const matches = profile.deals.filter(
    (deal) => deal.properties.carerix_first_meeting_date && currentPhase.id.some((id) => id === Number(deal.properties.dealstage))
  )
  return (
    <section data-component="HubspotMatches" className="overflow-x-clip">
      {matches.map((deal, index) => (
        // @ts-ignore
        <Match deal={deal} index={index} fields={fields} key={deal.id} />
      ))}
    </section>
  )
}

export function Match({
  deal,
  index,
  fields
}: {
  deal: AlteredSimplePublicObjectWithAssociations
  index: number
  fields: Page_Flexcontent_Flex_Hubspot
}) {
  useEffect(() => {
    var embed = `<iframe class='relative z-20' width='100%' height='100%' frameborder='0' scrolling='no' marginheight='0' marginwidth='0' src='https://maps.google.com/maps?&amp;q=${encodeURIComponent(
      deal.properties.carerix_first_meeting_location
    )}&amp;output=embed'></iframe>`

    const doc = document.querySelector(`#maps-${deal.id}`)

    if (doc) {
      doc.innerHTML = embed
    }
  }, [deal.id, deal.properties.carerix_first_meeting_location])

  return (
    <div
      id={deal.id}
      key={deal.id}
      className={clsx(index % 2 === 0 ? 'bg-site-secondair' : 'bg-white', 'section--large--pt section--large--pb')}
    >
      <div className="container">
        <div className="max-w-[1119px] mx-auto">
          <div>
            <Animated delay={100}>
              <h2 className="border-text relative z-30 mb-4 md:-mb-2 lg:-mb-4 max-sm:!leading-[1.2] title--semi-unit text-transparent md:text-center">
                {fields.title}
              </h2>
            </Animated>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-16 lg:gap-x-24">
            <div className={clsx(index % 2 === 0 ? '' : 'order-last', 'max-md:order-last')}>
              <Animated delay={150}>
                <div className="relative h-[400px] w-full">
                  <Animated delay={300}>
                    <div
                      className={clsx(
                        index % 2 === 0 ? '-left-[57px]' : '-right-[57px]',
                        'h-[76px] w-[114px] z-20 bg-site-accent absolute top-[38px] lg:-top-[38px]'
                      )}
                    />
                  </Animated>

                  <div className="absolute w-full h-full z-10 left-0 top-0 bg-site-grey" />
                  <div className="w-full h-full" id={`maps-${deal.id}`} />
                </div>
              </Animated>
            </div>
            <Animated delay={300}>
              <div className="flex flex-col py-6 justify-center gap-y-6 sm:gap-y-10">
                <Content className="children-p:text-lg children-strong:text-site-base">
                  {fields.description
                    ?.replace(
                      '{meeting_time}',
                      `${dayjs(deal.properties.carerix_first_meeting_date)
                        .format('dddd DD MMMM [om] HH:mm')
                        .replace(/^\w/, (c) => c.toUpperCase())} uur`
                    )
                    ?.replaceAll('{meeting_name}', deal.properties.carerix_first_meeting_location_name)
                    ?.replace('{meeting_location}', deal.properties.carerix_first_meeting_location)
                    ?.replace('{meeting_city}', deal.properties.carerix_first_meeting_city)
                    ?.replace(
                      '{meeting_contact_name}',
                      deal.properties.carerix_first_meeting_contact_firstname
                        ? `${deal.properties.carerix_first_meeting_contact_firstname} ${deal.properties.carerix_first_meeting_contact_lastname}`
                        : `een vertegenwoordiger van het bedrijf`
                    )}
                </Content>
                <div className="flex sm:flex-row flex-col items-center gap-y-2 gap-x-4">
                  <LosseLink
                    target="_blank"
                    to={`https://www.google.com/maps/dir//${deal.properties.carerix_first_meeting_location}`}
                    className="btn--conversion max-sm:w-full"
                  >
                    Plan je route in
                  </LosseLink>
                  {deal.company.website && (
                    <LosseLink target="_blank" to={deal.company.website} className="btn--outline--black max-sm:w-full">
                      Bekijk website
                    </LosseLink>
                  )}
                </div>
              </div>
            </Animated>
          </div>
        </div>
      </div>
    </div>
  )
}
